import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Alert from '@material-ui/lab/Alert'
import CircularProgress from '@material-ui/core/CircularProgress'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import ConfirmationNumberOutlinedIcon from 'components/material-symbol/icons/ConfirmationNumber'
import RedeemIcon from 'components/material-symbol/icons/Redeem'
import IconButton from '@material-ui/core/IconButton'
import { useHistory } from 'react-router-dom'
import { useIsMobile } from 'lib/hooks'
import EmailFieldWithSuggestions from 'components/form-util/email-field-with-suggestions'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import { useStyles } from './styles'
import { buildLessonPackBookingUrls } from '../lesson-pack-action-utils'

const GIFT_LESSON_PACK = gql`
  mutation GiftLessonPack($input: TransferLessonCreditInput!) {
    transferLessonCredit(input: $input) {
      success
      message
    }
  }
`

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email')
    .required('Email is required'),
  confirmAccuracy: yup
    .bool()
    .oneOf(
      [true],
      'Please confirm the accuracy of the above information by checking this box.',
    ),
})

const fieldProps = classes => ({
  variant: 'outlined',
  fullWidth: true,
  margin: 'none',
  className: classes.formField,
})

const GiftLessonPackModal = ({ open, onClose, lessonPack }) => {
  const classes = useStyles()
  const titleClasses = useDialogTitleStyles()
  const sharedFieldProps = fieldProps(classes)
  const history = useHistory()
  const isMobile = useIsMobile()
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      confirmAccuracy: false,
    },
  })

  const [giftLessonPack, { loading: mutationLoading }] = useMutation(
    GIFT_LESSON_PACK,
    {
      onCompleted: data => {
        setSuccess(data.transferLessonCredit.success)
        setError(
          data.transferLessonCredit.success
            ? ''
            : data.transferLessonCredit.message,
        )
      },
    },
  )

  const onSubmit = async values => {
    setError('')
    return giftLessonPack({
      variables: {
        input: {
          lessonPackId: lessonPack.id,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          lessonCreditCount: parseInt(lessonPack.totalCredits),
        },
      },
    })
  }

  const handleClose = () => {
    reset()
    setError('')
    setSuccess(false)
    onClose()
  }

  const purchaseMoreBookingUrls =
    lessonPack &&
    buildLessonPackBookingUrls(
      lessonPack.lessonType,
      lessonPack.remainingCredits,
    )

  const handlePurchaseMore = () => {
    if (!purchaseMoreBookingUrls) {
      return
    }

    history.push(purchaseMoreBookingUrls.buyMoreVouchersUrl)
    handleClose()
  }

  const handleHome = () => {
    history.push('/club')
    handleClose()
  }

  if (success) {
    return (
      <Dialog
        isOpen={open}
        onClose={handleClose}
        showCloseButton
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        classes={{ paper: titleClasses.dialogPaper }}
      >
        {() => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: titleClasses.titleRoot }}
            >
              <Typography component="h2" className={titleClasses.title}>
                Gift sent
              </Typography>
            </DialogTitle>
            <DialogContent
              classes={{
                root: clsx(titleClasses.content, classes.dialogContent),
              }}
            >
              <Box className={classes.successContent}>
                <IconButton
                  className={classes.celebrationIcon}
                  disableRipple
                  tabIndex={-1}
                  aria-hidden
                >
                  <CelebrationOutlinedIcon fontSize="large" />
                </IconButton>
                <Typography className={classes.successTitle}>
                  Success!
                </Typography>
                <Typography className={titleClasses.bodyText}>
                  The recipient has been notified to accept the lesson pack. You
                  will receive a confirmation once the credits are successfully
                  transferred to their account. Please note, the acceptance link
                  will expire in 7 days. If it is not accepted by then,
                  you&apos;ll receive a notice when the pack is added back to
                  your account.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions className={titleClasses.actions}>
              <Button color="primary" size="large" onClick={handleHome}>
                Home
              </Button>
              {purchaseMoreBookingUrls ? (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  disableElevation
                  onClick={handlePurchaseMore}
                  startIcon={
                    <ConfirmationNumberOutlinedIcon
                      fontSize="small"
                      opticalSize={20}
                      aria-hidden
                    />
                  }
                >
                  Purchase Another Pack
                </Button>
              ) : null}
            </DialogActions>
          </>
        )}
      </Dialog>
    )
  }

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      showCloseButton
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      disableClose={mutationLoading}
      classes={{ paper: titleClasses.dialogPaper }}
    >
      {() => (
        <>
          <DialogTitle
            disableTypography
            classes={{ root: titleClasses.titleRoot }}
          >
            <Typography component="h2" className={titleClasses.title}>
              Gift Lesson Pack
            </Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent
              classes={{
                root: clsx(titleClasses.content, classes.dialogContent),
              }}
            >
              <Typography className={classes.bodyText}>
                If you wish to cancel the pack before acceptance, please reach
                out to support at support@pga.org. Once accepted, it&apos;s
                final. <strong>The recipient will be notified.</strong>
              </Typography>

              {error ? (
                <Alert severity="error" className={classes.alert}>
                  {error}
                </Alert>
              ) : null}

              <Box className={classes.fieldStack}>
                <Box className={classes.fieldRow}>
                  <TextField
                    name="firstName"
                    inputRef={register}
                    label="Recipient first name *"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    {...sharedFieldProps}
                  />
                  <TextField
                    name="lastName"
                    inputRef={register}
                    label="Recipient last name *"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    {...sharedFieldProps}
                  />
                </Box>

                <EmailFieldWithSuggestions
                  label="Recipient email *"
                  control={control}
                  name="email"
                  errors={errors}
                  classes={classes}
                  variant="outlined"
                  margin="none"
                  className={classes.formField}
                />

                <Controller
                  name="confirmAccuracy"
                  control={control}
                  render={({ onChange, value, ref }) => (
                    <Box className={classes.confirmationField}>
                      <FormControlLabel
                        className={classes.confirmationLabel}
                        control={
                          <Checkbox
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                            inputRef={ref}
                            color="default"
                            size="small"
                            classes={{
                              root: classes.confirmationCheckboxRoot,
                              checked: classes.confirmationCheckboxChecked,
                            }}
                          />
                        }
                        label={
                          <Typography className={classes.confirmationText}>
                            I confirm the accuracy of the above information by
                            checking this box.
                          </Typography>
                        }
                      />
                      {errors.confirmAccuracy ? (
                        <FormHelperText
                          error
                          className={classes.confirmationError}
                        >
                          {errors.confirmAccuracy.message}
                        </FormHelperText>
                      ) : null}
                    </Box>
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions className={titleClasses.actions}>
              <Button
                color="primary"
                size="large"
                onClick={handleClose}
                disabled={mutationLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disableElevation
                disabled={mutationLoading}
                startIcon={
                  !mutationLoading ? (
                    <RedeemIcon fontSize="small" opticalSize={20} aria-hidden />
                  ) : (
                    undefined
                  )
                }
              >
                {mutationLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Gift'
                )}
              </Button>
            </DialogActions>
          </form>
        </>
      )}
    </Dialog>
  )
}

export default GiftLessonPackModal
